import { ActionTree } from 'vuex';
import api from '@/api';
import { ALL_LOGGED_USER as ALL_PERMISSIONS } from '@/api/requests/permissions';
import {
    ALL as ALL_PREFERENCES,
    FIND as FIND_PREFERENCE,
    SAVE as SET_PREFERENCE,
    DELETE as RESET_PREFERENCE,
} from '@/api/requests/preferences';
import * as settingsApi from "@/api/requests/settings";
import { TENANT_INFO } from '@/api/requests/tenant/tenants';
import { CHECK_PASSWORD, USER_INFO } from '@/api/requests/auth/auth';
import { AuthRequest } from '@/api/requests/auth/types';
import ManuteAuthToken from '@/api/src/token';
import ManuteAuth from '@/api/src/auth';
import { RootState } from '@/store/types';
import { OptionRequest, PasswordResetRequest } from '@/api/requests/types';
import * as PasswordResetRequests from '@/api/requests/password-resets';

export const actions: ActionTree<RootState, RootState> = {
    /**
     * Attempts login.
     *
     * @param dispatch
     * @param credentials
     */
    async login({ dispatch }, credentials: AuthRequest) {
        // Authenticate user.
        await ManuteAuth.authenticate(credentials);
        // Initialize app.
        await dispatch('initApp');
    },

    /**
     * Initialize global application state.
     *
     * @param commit
     * @param dispatch
     */
    async initApp({ commit, dispatch }) {
        await dispatch('initTenant');
        await dispatch('initAuth');
        commit('setAppLoaded', true);
    },

    /**
     * Set tenant.
     *
     * @param commit
     */
    async initTenant({ commit }) {
        try {
            const response = await api.call(TENANT_INFO());
            commit('setTenant', response.data.data);
        } catch (e) {
            commit('resetTenant');
        }
    },

    /**
     * Initialize authentication state.
     *
     * @param dispatch
     */
    async initAuth({ dispatch }) {
        try {
            // Setting system settings.
            await dispatch('initSettings');
            // Calling this method, if returns 401 Unauthenticated, axios interceptor will refresh the token.
            await dispatch('initUser');
            // Setting user permissions.
            await dispatch('initPermissions');
            // Setting user preferences.
            await dispatch('initPreferences');
        } catch (e) {
            dispatch('resetAuth');
        }
    },

    /**
     * Reset authentication state.
     *
     * @param commit
     */
    resetAuth({ commit }) {
        // Remove the token from local storage.
        ManuteAuthToken.removeFromStorage();
        // Reset settings in the store.
        commit('resetSettings');
        // Reset user in the store.
        commit('resetUser');
        // Reset permissions in the store.
        commit('resetPermissions');
        // Reset preferences in the store.
        commit('resetPreferences');
    },

    /**
     * Set authenticated user.
     *
     * @param commit
     */
    async initUser({ commit }) {
        const response = await api.call(USER_INFO());
        commit('setUser', response.data.data);
    },

    /**
     * Set authenticated user's permissions.
     *
     * @param commit
     */
    async initPermissions({ commit }) {
        const response = await api.call(ALL_PERMISSIONS());
        commit('setPermissions', response.data.data);
    },

    /**
     * Set authenticated user's preferences.
     *
     * @param commit
     */
    async initPreferences({ commit }) {
        const response = await api.call(ALL_PREFERENCES());
        commit('setPreferences', response.data.data);
    },

    /**
     * Set app settings.
     *
     * @param commit
     */
    async initSettings({ commit }) {
        const response = await api.call(settingsApi.ALL());
        commit('setSettings', response.data.data);
    },

    /**
     * Set authenticated user's preferences.
     *
     * @param commit
     * @param preference
     */
    async setPreference({ commit }, preference: OptionRequest) {
        const response = await api.call(SET_PREFERENCE(preference));
        commit('setPreference', response.data.data);
    },

    /**
     * Set app setting.
     *
     * @param commit
     * @param setting
     */
    async setSetting({ commit }, setting: OptionRequest) {
        const response = await api.call(settingsApi.SAVE(setting));
        commit('setSetting', response.data.data);
    },

    /**
     * Add a generic model with it's route to the list of generic models.
     *
     * @param commit
     * @param params
     */
    addGenericModel({ commit }, params: {genericModel: Object, genericModelRoute: string}) {
        commit('addGenericModel', params);
    },

    /**
     * Remove last generic model to the list of generic models.
     *
     * @param commit
     */
    removeLastGenericModel({ commit }) {
        commit('removeLastGenericModel');
    },

    /**
     * Remove last generic model's route to the list of generic model routes.
     *
     * @param commit
     */
    removeLastGenericModelRoute({ commit }) {
        commit('removeLastGenericModelRoute');
    },
    
    /**
     * Reset the list of generic models.
     * @param state
     */
    resetGenericModels({ commit }) {
        commit('resetGenericModels');
    },

    /**
     * Reset the list of generic model routes.
     * @param state
     */
    resetGenericModelRoutes({ commit }) {
        commit('resetGenericModelRoutes');
    },

    /**
     * Reset generic models and generic model routes.
     * @param state
     */
    resetGenericModelsAndRoutes({ commit }) {
        commit('resetGenericModels');
        commit('resetGenericModelRoutes');
    },

    /**
     * Init authenticated user's preference.
     *
     * @param commit
     * @param key
     */
    async findPreference({ commit }, key: string) {
        try {
            const response = await api.call(FIND_PREFERENCE(key));
            commit('setPreference', response.data.data);
        } catch (e) {
            commit('setPreference', { key, value: null });
        }
    },

    /**
     * Find app setting.
     *
     * @param commit
     * @param key
     */
    async findSetting({ commit }, key: string) {
        try {
            const response = await api.call(settingsApi.FIND(key));
            commit('setSetting', response.data.data);
        } catch (e) {
            commit('setSetting', { key, value: null });
        }
    },

    /**
     * Reset authenticated user's preferences.
     *
     * @param commit
     * @param key
     */
    async resetPreference({ commit }, key: string) {
        await api.call(RESET_PREFERENCE(key));
        commit('resetPreference', key);
    },

    /**
     * Reset setting.
     *
     * @param commit
     * @param key
     */
    async resetSetting({ commit }, key: string) {
        await api.call(settingsApi.DELETE(key));
        commit('resetSetting', key);
    },

    /**
     * Send email to reset user's password.
     *
     * @param commit
     */
     async sendResetEmail({ commit }, email: string) {
        const response = await api.call(PasswordResetRequests.CREATE(email));
    },

    /**
     * Find user's reset password token
     *
     * @param commit
     */
    async findToken({ commit }, token: string) {
        const response = await api.call(PasswordResetRequests.FIND(token))
        .catch((e) => {
            return e;
        });
        return response;
    },

    /**
     * Change user password
     *
     * @param commit
     */
    async changePassword({ commit }, request: PasswordResetRequest) {
        const response = await api.call(PasswordResetRequests.RESET(request));
    },
};
